import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

/**
 * The real operational health of ONE connection, read from the three ledgers
 * that actually record what happened: `analytics_fetch_runs` (did the fetcher
 * run and did it succeed), `social_post_targets` (did a post reach the
 * platform), and `account_metrics_daily` (which days do we actually hold data
 * for).
 *
 * 🔴 Why this exists. `deriveConnectionStatus` answers "is the stored token
 * usable" — a green dot means the credential is fine, nothing more. On
 * production right now that green dot is worn by a Facebook connection whose
 * every publish attempt has been rejected (7 of 7), and by a TikTok connection
 * whose analytics fetcher has failed 56 consecutive runs and has NEVER
 * succeeded. A settings panel that shows those as simply "Connected" is lying
 * by omission. Token health and operational health are different questions and
 * this hook answers the second one.
 *
 * 🔴 Identity: every table read here uses the account-owner policy plus the
 * exact connection/resource identity supplied by the detail view. This keeps
 * a sibling workspace's run, publish, or coverage history out of a provider
 * detail card.
 */

export type SyncRunStatus = 'succeeded' | 'failed' | 'skipped';

/** `analytics_fetch_runs.failure_kind` — the CHECK constraint's exact 3 values. */
export type FetchRunFailureKind = 'auth' | 'transient' | 'other';

const FETCH_RUN_FAILURE_KINDS: ReadonlySet<string> = new Set(['auth', 'transient', 'other']);

/** Narrows the DB's untyped `text` column to the constrained set, or null. */
export function toFetchRunFailureKind(raw: string | null | undefined): FetchRunFailureKind | null {
  return raw && FETCH_RUN_FAILURE_KINDS.has(raw) ? (raw as FetchRunFailureKind) : null;
}

export interface IntegrationSyncHealth {
  /** The most recent run of any outcome, or null when the fetcher has never run. */
  lastRunAt: string | null;
  lastRunStatus: SyncRunStatus | null;
  /** The most recent SUCCESSFUL run. Null means it has never once succeeded. */
  lastSuccessAt: string | null;
  /** Failed runs recorded since the last success (or ever, if never successful). */
  failuresSinceSuccess: number;
  /** The fetcher's own words for the most recent failure. Never paraphrased. */
  lastErrorSummary: string | null;
  /**
   * How `complete_analytics_fetch_run` classified the CURRENT `lastRunStatus
   * === 'failed'` row. Null when the last run succeeded, or the row predates
   * classification. `'auth'` is the one kind a fresh, successful reconnect
   * can retroactively invalidate — see `isPreReconnectAuthFailure` in
   * `IntegrationDetail.tsx`.
   */
  lastRunFailureKind: FetchRunFailureKind | null;
}

export interface IntegrationPublishHealth {
  published: number;
  failed: number;
  lastPublishedAt: string | null;
  lastFailure: { message: string | null; code: string | null; at: string | null } | null;
}

export interface IntegrationCoverage {
  firstDay: string;
  lastDay: string;
  /** Days between firstDay and today, inclusive — the window the strip draws. */
  spanDays: number;
  /** Days inside that window Mora actually holds a row for. */
  daysPresent: number;
  /** ISO `yyyy-MM-dd` keys, one per day held. */
  days: string[];
}

export interface IntegrationHealth {
  sync: IntegrationSyncHealth | null;
  publish: IntegrationPublishHealth | null;
  coverage: IntegrationCoverage | null;
}

/** Coverage never draws more than this many day cells — beyond it the strip is noise. */
const MAX_COVERAGE_DAYS = 180;

function dayKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

async function fetchSyncHealth(
  platform: string,
  connectionId: string | null,
): Promise<IntegrationSyncHealth | null> {
  if (!connectionId) return null;
  const [{ data: latest }, { data: latestSuccess }] = await Promise.all([
    supabase
      .from('analytics_fetch_runs')
      .select('status, started_at, finished_at, error_summary, failure_kind')
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .order('started_at', { ascending: false })
      .limit(1),
    supabase
      .from('analytics_fetch_runs')
      .select('started_at, finished_at')
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .eq('status', 'succeeded')
      .order('started_at', { ascending: false })
      .limit(1),
  ]);

  const last = latest?.[0];
  if (!last) return null;

  const success = latestSuccess?.[0] ?? null;
  const lastSuccessAt = success?.finished_at ?? success?.started_at ?? null;

  let failuresSinceSuccess = 0;
  let lastErrorSummary: string | null = null;
  let lastRunFailureKind: FetchRunFailureKind | null = null;
  if (last.status !== 'succeeded') {
    const failedQuery = supabase
      .from('analytics_fetch_runs')
      .select('id', { count: 'exact', head: true })
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .eq('status', 'failed');
    const { count } = success?.started_at
      ? await failedQuery.gt('started_at', success.started_at)
      : await failedQuery;
    failuresSinceSuccess = count ?? 0;
    lastErrorSummary = last.error_summary ?? null;
    lastRunFailureKind = toFetchRunFailureKind(last.failure_kind);
  }

  return {
    lastRunAt: last.finished_at ?? last.started_at ?? null,
    lastRunStatus: (last.status as SyncRunStatus | null) ?? null,
    lastSuccessAt,
    failuresSinceSuccess,
    lastErrorSummary,
    lastRunFailureKind,
  };
}

async function fetchPublishHealth(
  platform: string,
  connectionId: string | null,
): Promise<IntegrationPublishHealth | null> {
  if (!connectionId) return null;
  const [publishedCount, failedCount, lastPublished, lastFailed] = await Promise.all([
    supabase
      .from('social_post_targets')
      .select('id', { count: 'exact', head: true })
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .in('delivery_status', ['published', 'published_with_warnings']),
    supabase
      .from('social_post_targets')
      .select('id', { count: 'exact', head: true })
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .eq('delivery_status', 'failed'),
    supabase
      .from('social_post_targets')
      .select('published_at')
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .in('delivery_status', ['published', 'published_with_warnings'])
      .order('published_at', { ascending: false })
      .limit(1),
    supabase
      .from('social_post_targets')
      .select('error_message, error_code, updated_at')
      .eq('platform', platform)
      .eq('connection_id', connectionId)
      .eq('delivery_status', 'failed')
      .order('updated_at', { ascending: false })
      .limit(1),
  ]);

  const published = publishedCount.count ?? 0;
  const failed = failedCount.count ?? 0;
  // No attempt of either outcome means publishing has no story to tell yet —
  // the section renders its own "nothing published yet" line rather than a
  // pair of zeroes dressed up as a report.
  if (published === 0 && failed === 0) {
    return { published: 0, failed: 0, lastPublishedAt: null, lastFailure: null };
  }

  const failureRow = lastFailed.data?.[0];
  return {
    published,
    failed,
    lastPublishedAt: lastPublished.data?.[0]?.published_at ?? null,
    lastFailure: failureRow
      ? {
          message: failureRow.error_message ?? null,
          code: failureRow.error_code ?? null,
          at: failureRow.updated_at ?? null,
        }
      : null,
  };
}

async function fetchCoverage(
  platform: string,
  platformAccountIds: readonly string[],
): Promise<IntegrationCoverage | null> {
  if (platformAccountIds.length === 0) return null;
  const windowStart = new Date(Date.now() - (MAX_COVERAGE_DAYS - 1) * 86_400_000);
  const { data } = await supabase
    .from('account_metrics_daily')
    .select('metric_day')
    .eq('platform', platform)
    .in('platform_account_id', platformAccountIds)
    .gte('metric_day', dayKey(windowStart))
    .order('metric_day', { ascending: true });

  const days = Array.from(new Set((data ?? []).map((row) => String(row.metric_day))));
  if (days.length === 0) return null;

  const firstDay = days[0];
  const lastDay = dayKey(new Date());
  return {
    firstDay,
    lastDay,
    spanDays: Math.max(daysBetween(firstDay, lastDay), days.length),
    daysPresent: days.length,
    days,
  };
}

export interface UseIntegrationHealthOptions {
  /** Does this channel have a scheduled fetcher at all. */
  syncs: boolean;
  /** Can Mora publish here at all. */
  publishes: boolean;
  /** Does this channel write day rows to `account_metrics_daily`. */
  dailyCoverage: boolean;
  /** Exact connection shown by the detail view. */
  connectionId?: string | null;
  /** Provider identities represented by this connection's selected ledger. */
  platformAccountIds?: readonly string[];
}

/**
 * Read one connection's operational health. Only the ledgers the channel can
 * truthfully appear in are queried — asking `social_post_targets` about Google
 * Analytics would return an honest zero that the UI would then have to explain
 * away, so it is never asked.
 *
 * `platform` of `null` disables the query entirely (no detail view is open).
 */
export function integrationHealthQueryOptions(
  platform: string | null,
  options: UseIntegrationHealthOptions,
) {
  const { syncs, publishes, dailyCoverage } = options;
  const connectionId = options.connectionId ?? null;
  const platformAccountIds = [...(options.platformAccountIds ?? [])].sort();
  return {
    queryKey: ['integration-health', platform, connectionId, platformAccountIds] as const,
    enabled: Boolean(platform),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<IntegrationHealth> => {
      const key = platform as string;
      const [sync, publish, coverage] = await Promise.all([
        syncs ? fetchSyncHealth(key, connectionId) : Promise.resolve(null),
        publishes ? fetchPublishHealth(key, connectionId) : Promise.resolve(null),
        dailyCoverage ? fetchCoverage(key, platformAccountIds) : Promise.resolve(null),
      ]);
      return { sync, publish, coverage };
    },
  };
}

export function useIntegrationHealth(
  platform: string | null,
  options: UseIntegrationHealthOptions,
) {
  return useQuery<IntegrationHealth>(integrationHealthQueryOptions(platform, options));
}
